// src/components/transport/ZonesTransport.jsx
// ============================================================================
// Les zones de trajet du bus et leur grille de tarifs (lot 3a bis)
// ============================================================================
//
// Une carte de la page « Paramétrage de la scolarité », sous les réglages du
// bus. Décisions de Daniel du 03/11/2026 : le prix du bus suit la zone du
// trajet de l'élève ; tant qu'aucune zone n'est active, le tarif de la classe
// vaut. Chaque geste est journalisé par le serveur.
//
// « Changer le tarif » vaut à partir d'un mois choisi : les mensualités non
// payées de la zone à partir de ce mois passent au nouveau tarif, chacune avec
// son sens ; celles d'avant gardent le leur ; un mois payé ne bouge pas. Une
// zone ne se supprime pas : elle se désactive, une fois vide d'abonnés au mois.
//
// Absente tant que les zones ne sont pas installées (migration).
// ============================================================================

import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, MapPin, Plus, Save } from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../ui/dialog';
import { Button } from '../ui/button';
import { BOUTON_CONTOUR, BOUTON_PLEIN } from '../ui/boutons';
import ListeDeroulante from '../ui/ListeDeroulante';
import PorteurNotification from '../ui/PorteurNotification';
import EnTeteSection from '../parametrage/EnTeteSection';
import CarteReglages from '../parametrage/CarteReglages';
import { Encadre } from '../parametrage/PiecesReglage';
import ConfirmDialog from '../ConfirmDialog';
import { fcfa, transport } from './apiTransport';

const BOUTON_PIED = 'h-9 min-w-[168px] justify-center gap-2';
const CHAMP = 'h-9 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-800 outline-none transition focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15';
const ACCENT = '#319190';

const Fenetre = ({ titre, description, onFermer, envoi, erreur, effacerErreur, actions, children }) => (
  <Dialog open onOpenChange={(o) => { if (!o && !envoi) onFermer(); }}>
    <DialogContent className="w-[95vw] max-w-2xl !max-h-[92vh] overflow-hidden flex flex-col p-0 gap-0">
      <DialogHeader className="flex-row items-center gap-3 space-y-0 border-b px-5 py-4 text-left">
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#246990] text-white">
          <MapPin className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <DialogTitle className="text-lg font-semibold text-gray-900">{titre}</DialogTitle>
          {description && <DialogDescription className="text-sm text-gray-500">{description}</DialogDescription>}
        </div>
      </DialogHeader>
      <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
        <PorteurNotification notification={erreur ? { message: erreur, type: 'error' } : null} onFermer={effacerErreur} />
        {children}
      </div>
      <DialogFooter className="gap-2 border-t bg-gray-50 px-5 py-4 sm:space-x-0">{actions}</DialogFooter>
    </DialogContent>
  </Dialog>
);

const FenetreZone = ({ zone, onFermer, onFait }) => {
  const creation = !zone;
  const [nom, setNom] = useState(zone?.nom || '');
  const [description, setDescription] = useState(zone?.description || '');
  const [tarif, setTarif] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState(null);
  const pret = nom.trim().length >= 2 && (!creation || Number(tarif) > 0);

  const enregistrer = async () => {
    if (!pret || envoi) return;
    setEnvoi(true);
    setErreur(null);
    try {
      if (creation) await transport.creerZone({ nom: nom.trim(), description: description.trim(), tarif_mensuel: Number(tarif) });
      else await transport.modifierZone(zone.id, { nom: nom.trim(), description: description.trim() });
      onFait(creation ? `Zone « ${nom.trim()} » créée.` : 'Zone modifiée.');
    } catch (e) {
      setErreur(e.message);
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <Fenetre
      titre={creation ? 'Nouvelle zone de trajet' : `Modifier la zone ${zone.nom}`}
      description={creation ? 'Un groupe de quartiers desservis au même prix.' : 'Le nom et la description ; le tarif se change à part.'}
      onFermer={onFermer}
      envoi={envoi}
      erreur={erreur}
      effacerErreur={() => setErreur(null)}
      actions={(
        <>
          <Button size="default" type="button" className={`${BOUTON_CONTOUR} ${BOUTON_PIED}`} onClick={onFermer} disabled={envoi}>Annuler</Button>
          <Button size="default" type="button" className={`${BOUTON_PIED} ${BOUTON_PLEIN}`} onClick={enregistrer} disabled={!pret || envoi}>
            {envoi ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Enregistrer
          </Button>
        </>
      )}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor="zone-nom" className="mb-1 block text-sm text-gray-700">Nom de la zone</label>
          <input id="zone-nom" type="text" maxLength={80} autoComplete="off" className={CHAMP} value={nom} onChange={(e) => setNom(e.target.value)} disabled={envoi} placeholder="Zone A" />
        </div>
        {creation && (
          <div>
            <label htmlFor="zone-tarif" className="mb-1 block text-sm text-gray-700">Tarif mensuel, aller et retour</label>
            <div className="relative">
              <input
                id="zone-tarif" type="text" inputMode="numeric" autoComplete="off" className={`${CHAMP} pr-16 text-right`}
                value={tarif} onChange={(e) => setTarif(e.target.value.replace(/[^\d]/g, ''))} disabled={envoi}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">FCFA</span>
            </div>
          </div>
        )}
      </div>
      <div>
        <label htmlFor="zone-description" className="mb-1 block text-sm text-gray-700">Quartiers desservis (facultatif)</label>
        <input id="zone-description" type="text" maxLength={255} autoComplete="off" className={CHAMP} value={description} onChange={(e) => setDescription(e.target.value)} disabled={envoi} placeholder="Moungali, Poto-Poto, Ouenzé" />
      </div>
    </Fenetre>
  );
};

const FenetreTarif = ({ zone, mois, moisParDefaut, onFermer, onFait }) => {
  const [tarif, setTarif] = useState('');
  const [effet, setEffet] = useState(moisParDefaut ? String(moisParDefaut) : (mois[0] ? String(mois[0].id) : ''));
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState(null);
  const pret = Number(tarif) > 0 && effet;

  const enregistrer = async () => {
    if (!pret || envoi) return;
    setEnvoi(true);
    setErreur(null);
    try {
      const r = await transport.changerTarifZone(zone.id, { tarif_mensuel: Number(tarif), mois_effet_id: Number(effet) });
      const n = r.lignes?.mises_a_jour || 0;
      onFait(`${zone.nom} : ${fcfa(r.tarif)} à partir de ${r.mois_effet?.nom}. `
        + (n ? `${n} mensualité${n > 1 ? 's' : ''} mise${n > 1 ? 's' : ''} à jour (${r.lignes.eleves} élève${r.lignes.eleves > 1 ? 's' : ''}).` : 'Aucune mensualité à rechiffrer.'));
    } catch (e) {
      setErreur(e.message);
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <Fenetre
      titre={`Changer le tarif de ${zone.nom}`}
      description={`Aujourd'hui : ${fcfa(zone.tarif)} par mois, aller et retour.`}
      onFermer={onFermer}
      envoi={envoi}
      erreur={erreur}
      effacerErreur={() => setErreur(null)}
      actions={(
        <>
          <Button size="default" type="button" className={`${BOUTON_CONTOUR} ${BOUTON_PIED}`} onClick={onFermer} disabled={envoi}>Annuler</Button>
          <Button size="default" type="button" className={`${BOUTON_PIED} ${BOUTON_PLEIN}`} onClick={enregistrer} disabled={!pret || envoi}>
            {envoi ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Appliquer
          </Button>
        </>
      )}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor="tarif-nouveau" className="mb-1 block text-sm text-gray-700">Nouveau tarif mensuel</label>
          <div className="relative">
            <input
              id="tarif-nouveau" type="text" inputMode="numeric" autoComplete="off" className={`${CHAMP} pr-16 text-right`}
              value={tarif} onChange={(e) => setTarif(e.target.value.replace(/[^\d]/g, ''))} disabled={envoi}
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">FCFA</span>
          </div>
        </div>
        <div>
          <label htmlFor="tarif-effet" className="mb-1 block text-sm text-gray-700">À partir de</label>
          <ListeDeroulante
            id="tarif-effet"
            valeur={effet}
            onChange={setEffet}
            desactive={envoi}
            options={mois.map((m) => ({ valeur: String(m.id), libelle: m.nom }))}
          />
        </div>
      </div>
      <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs leading-relaxed text-amber-900">
        Les mensualités non payées de cette zone à partir de ce mois passent au nouveau tarif, chacune selon son sens ;
        celles d'avant gardent le leur. Un mois déjà payé, même en partie, ne bouge pas, et un prix dérogatoire reste
        celui accordé. {zone.abonnes ? `${zone.abonnes} élève${zone.abonnes > 1 ? 's' : ''} abonné${zone.abonnes > 1 ? 's' : ''} au mois dans cette zone.` : ''}
      </p>
    </Fenetre>
  );
};

const ZonesTransport = () => {
  const [donnees, setDonnees] = useState(null);
  const [fenetre, setFenetre] = useState(null);
  const [aDesactiver, setADesactiver] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  const [toast, setToast] = useState(null);

  const charger = useCallback(async () => {
    try {
      setDonnees(await transport.zones());
    } catch (e) {
      setDonnees({ installe: false });
      if (e.statut !== 403) setToast({ message: e.message, type: 'error' });
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  if (!donnees || !donnees.installe) return <PorteurNotification notification={toast} onFermer={() => setToast(null)} />;

  const fini = (message) => {
    setFenetre(null);
    setToast({ message, type: 'success' });
    charger();
  };

  const basculer = async (zone, actif) => {
    setEnvoi(true);
    try {
      await transport.activerZone(zone.id, actif);
      setADesactiver(null);
      setToast({ message: actif ? `Zone « ${zone.nom} » réactivée.` : `Zone « ${zone.nom} » désactivée.`, type: 'success' });
      charger();
    } catch (e) {
      setToast({ message: e.message, type: 'error' });
    } finally {
      setEnvoi(false);
    }
  };

  const zones = donnees.zones || [];
  const actives = zones.filter((z) => z.actif).length;
  return (
    <div className="space-y-5 border-t border-gray-100 pt-6">
      <PorteurNotification notification={toast} onFermer={() => setToast(null)} />
      <EnTeteSection
        icone={MapPin}
        titre="Zones de trajet du bus"
        sousTitre={donnees.mode === 'zones'
          ? `Le prix du bus suit la zone du trajet de chaque élève - tarifs de l'année ${donnees.annee?.nom || ''}`
          : 'Aucune zone active : le prix du bus suit le tarif de chaque classe'}
        accent={ACCENT}
        accentSombre="#2a7a79"
        actions={(
          <button
            type="button"
            onClick={() => setFenetre({ type: 'zone', zone: null })}
            className="flex items-center gap-1.5 rounded-lg border border-transparent bg-[#319190] px-4 py-2 text-xs font-semibold text-white shadow-sm transition-all hover:shadow-md"
          >
            <Plus size={13} /> Ajouter une zone
          </button>
        )}
      />
      <CarteReglages titre="Zones" accent={ACCENT} compteur={`${actives} active${actives > 1 ? 's' : ''}`} dense>
        {zones.length === 0 && <p className="px-5 py-4 text-sm text-gray-500">Aucune zone pour l'instant.</p>}
        {zones.map((z) => (
          <div key={z.id} className={`flex flex-wrap items-center justify-between gap-3 px-5 py-4 ${z.actif ? 'bg-white hover:bg-gray-50/70' : 'bg-gray-50/40'}`}>
            <div className="flex min-w-[220px] flex-1 items-start gap-3">
              <div className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full" style={{ backgroundColor: z.actif ? ACCENT : '#9ca3af' }} />
              <div>
                <p className={`text-sm font-semibold ${z.actif ? 'text-gray-800' : 'text-gray-400'}`}>
                  {z.nom}
                  {!z.actif && <span className="ml-2 inline-flex h-6 items-center rounded-lg border border-gray-200 bg-gray-50 px-2.5 text-xs font-medium text-gray-500">Désactivée</span>}
                </p>
                {z.description && <p className="mt-0.5 text-[11px] text-gray-500">{z.description}</p>}
                <p className="mt-0.5 text-[11px] text-gray-400">
                  {z.abonnes} abonné{z.abonnes > 1 ? 's' : ''} au mois
                  {z.frise?.length > 1 ? ` - ${z.frise.map((t) => `${t.mois_effet || "début d'année"} : ${fcfa(t.tarif)}`).join(', puis ')}` : ''}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm font-semibold tabular-nums text-gray-900">{fcfa(z.tarif)} par mois</p>
              <p className="text-[11px] text-gray-400">un sens {fcfa(z.montants?.un_sens)} - ticket {fcfa(z.tickets?.journee)}</p>
            </div>
            <div className="flex flex-wrap justify-end gap-2">
              {z.actif && <Button type="button" size="sm" className={`${BOUTON_CONTOUR} w-[130px]`} onClick={() => setFenetre({ type: 'tarif', zone: z })}>Changer le tarif</Button>}
              <Button type="button" size="sm" className={`${BOUTON_CONTOUR} w-[100px]`} onClick={() => setFenetre({ type: 'zone', zone: z })}>Modifier</Button>
              {z.actif
                ? <Button type="button" size="sm" className={`${BOUTON_CONTOUR} w-[100px]`} onClick={() => setADesactiver(z)} disabled={envoi}>Désactiver</Button>
                : <Button type="button" size="sm" className={`${BOUTON_CONTOUR} w-[100px]`} onClick={() => basculer(z, true)} disabled={envoi}>Réactiver</Button>}
            </div>
          </div>
        ))}
      </CarteReglages>
      <Encadre titre="Comment le prix se calcule">
        Le tarif d'une zone est le prix d'un mois de bus, aller et retour. Un seul sens et le ticket en découlent (réglages
        ci-dessus). Dès qu'une zone est active, un abonnement au mois demande la zone et le lieu de prise en charge de l'élève.
      </Encadre>
      {fenetre?.type === 'zone' && <FenetreZone zone={fenetre.zone} onFermer={() => setFenetre(null)} onFait={fini} />}
      {fenetre?.type === 'tarif' && (
        <FenetreTarif zone={fenetre.zone} mois={donnees.mois || []} moisParDefaut={donnees.mois_par_defaut_id} onFermer={() => setFenetre(null)} onFait={fini} />
      )}
      <ConfirmDialog
        isOpen={Boolean(aDesactiver)}
        onClose={() => setADesactiver(null)}
        onConfirm={() => basculer(aDesactiver, false)}
        type="warning"
        title={aDesactiver ? `Désactiver la zone ${aDesactiver.nom}` : ''}
        description="Elle ne sera plus proposée aux abonnements ni aux tickets. Ses mensualités existantes gardent leur prix. Le serveur refuse tant qu'un abonné au mois y est rattaché."
        confirmText="Désactiver"
        cancelText="Retour"
        isLoading={envoi}
      />
    </div>
  );
};

export default ZonesTransport;
